import type { FileUIPart } from "ai";
import { extractSelectedSkills, normalizeSelectedSkills } from "./selected-skills";

/**
 * Simple localStorage-backed draft store keyed by session ID.
 *
 * Plain text drafts are persisted in localStorage.  Files cannot be put in
 * localStorage (and their blob URLs are volatile), so a send attempt keeps a
 * companion in-memory snapshot of the original File objects.  That snapshot
 * survives conversation navigation/remounts in the same tab and is cleared
 * only after the corresponding send is accepted.
 */

const STORAGE_KEY = "l9-chat-drafts";

/** Custom event dispatched on `window` whenever the draft map changes. */
export const DRAFTS_CHANGED_EVENT = "l9-drafts-changed";

export interface DraftSnapshot { text: string; selectedSkills: string[] }

export interface DraftMap {
  [sessionId: string]: string | DraftSnapshot | undefined;
}

export type DraftAttachment = FileUIPart & { rawFile?: File };

export interface DraftAttempt {
  id: string;
  text: string;
  files: DraftAttachment[];
  selectedSkills: string[];
}

const draftAttempts = new Map<string, DraftAttempt>();
let nextDraftAttemptId = 0;

function newDraftAttemptId(): string {
  nextDraftAttemptId += 1;
  return `draft-attempt-${Date.now().toString(36)}-${nextDraftAttemptId.toString(36)}`;
}

function readDrafts(): DraftMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return (JSON.parse(raw) as DraftMap) ?? {};
  } catch {
    return {};
  }
}

function normalizeDraft(value: unknown): DraftSnapshot {
  if (typeof value === "string") {
    const extracted = extractSelectedSkills(value);
    return { text: extracted.cleanText, selectedSkills: extracted.skills };
  }
  if (value && typeof value === "object" && "text" in value && typeof value.text === "string" && "selectedSkills" in value && Array.isArray(value.selectedSkills)) {
    return { text: value.text, selectedSkills: normalizeSelectedSkills(value.selectedSkills) };
  }
  return { text: "", selectedSkills: [] };
}

function writeDrafts(drafts: DraftMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // ignore storage errors (e.g. private mode, quota exceeded)
  }
}

/**
 * Dispatch a {@link DRAFTS_CHANGED_EVENT} on `window` so listeners
 * (e.g. the `useDraftSessionIds` hook in the sidebar) can re-read the
 * draft map and update the UI without polling.
 */
function notifyChange(): void {
  try {
    window.dispatchEvent(new CustomEvent(DRAFTS_CHANGED_EVENT));
  } catch {
    // ignore — non-DOM or restricted environments
  }
}

/**
 * Return the set of session IDs that currently have a non-empty draft.
 * Used by the sidebar to decide whether to show the draft indicator icon.
 */
export function getDraftSessionIds(): Set<string> {
  const drafts = readDrafts();
  const ids = new Set<string>();
  for (const [id, value] of Object.entries(drafts)) {
    const draft = normalizeDraft(value);
    if (draft.text.trim() !== "" || draft.selectedSkills.length > 0) {
      ids.add(id);
    }
  }
  return ids;
}

export function getDraft(sessionId: string): DraftSnapshot {
  return normalizeDraft(readDrafts()[sessionId]);
}

/**
 * Retain the complete payload of a send until the server accepts it.
 * `files` are copied shallowly so later UI mutations cannot alter the retry
 * snapshot, while the raw File references themselves remain available for a
 * fresh blob URL after the composer remounts.
 */
export function beginDraftAttempt(
  sessionId: string,
  text: string,
  files: DraftAttachment[] = [],
  selectedSkills: string[] = [],
): DraftAttempt {
  const attempt: DraftAttempt = {
    id: newDraftAttemptId(),
    text,
    files: files.map((file) => ({ ...file })),
    selectedSkills: normalizeSelectedSkills(selectedSkills),
  };
  draftAttempts.set(sessionId, attempt);
  setDraft(sessionId, text, attempt.selectedSkills);
  return attempt;
}

/** Return the current unsent payload for a session, if one is pending. */
export function getDraftAttempt(sessionId: string): DraftAttempt | null {
  return draftAttempts.get(sessionId) ?? null;
}

/** Return the attachment snapshot used to initialize a remounted composer. */
export function getDraftAttemptFiles(sessionId: string): DraftAttachment[] {
  return draftAttempts.get(sessionId)?.files ?? [];
}

/**
 * Mark a send attempt as accepted.  Do not erase a newer text draft that the
 * user typed while the request was in flight.
 */
export function completeDraftAttempt(sessionId: string, attemptId: string): void {
  const attempt = draftAttempts.get(sessionId);
  if (!attempt || attempt.id !== attemptId) return;

  draftAttempts.delete(sessionId);
  const current = getDraft(sessionId);
  if (current.text === attempt.text && current.selectedSkills.length === attempt.selectedSkills.length
    && current.selectedSkills.every((name, index) => name === attempt.selectedSkills[index])) {
    setDraft(sessionId, "", []);
  } else {
    // The text is newer, but listeners may still need to forget the
    // attachment snapshot that was just completed.
    notifyChange();
  }
}

export function setDraft(sessionId: string, text: string, selectedSkills: string[] = []): void {
  const drafts = readDrafts();
  const normalized = normalizeSelectedSkills(selectedSkills);
  if (text.trim() === "" && normalized.length === 0) {
    delete drafts[sessionId];
  } else {
    drafts[sessionId] = { text, selectedSkills: normalized };
  }
  writeDrafts(drafts);
  notifyChange();
}

export function clearDraft(sessionId: string): void {
  draftAttempts.delete(sessionId);
  const drafts = readDrafts();
  delete drafts[sessionId];
  writeDrafts(drafts);
  notifyChange();
}

export function clearAllDrafts(): void {
  draftAttempts.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  notifyChange();
}
